import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { ProductFormInput } from "../types";
import { createProduct } from "../services/inventory";
import { Modal } from "./Modal";

const emptyVariant = () => ({ colorName: "", colorCode: "#b8897c", minimumStock: 10, malhariaEnabled: false, galpao: 0, malharia: 0 });

export function ProductForm({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<ProductFormInput>({
    name: "", sku: "", category: "", description: "", notes: "", status: "ativo", variants: [emptyVariant()],
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await createProduct(form);
      toast.success("Produto cadastrado com estoque e histórico.");
      setForm({ name: "", sku: "", category: "", description: "", notes: "", status: "ativo", variants: [emptyVariant()] });
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível cadastrar o produto.");
    } finally { setSaving(false); }
  };

  const updateVariant = (index: number, changes: Partial<ProductFormInput["variants"][number]>) => {
    setForm((current) => ({ ...current, variants: current.variants.map((item, itemIndex) => itemIndex === index ? { ...item, ...changes } : item) }));
  };

  return (
    <Modal open={open} onClose={onClose} title="Cadastrar produto" description="Cada cor cria uma variação própria de estoque." wide>
      <form onSubmit={submit} className="form-stack">
        <div className="form-grid three">
          <label><span>Nome do produto *</span><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Ex.: Conjunto Tricot Mousse" /></label>
          <label><span>SKU base *</span><input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })} required placeholder="CM001" /></label>
          <label><span>Categoria *</span><input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required placeholder="Conjuntos" /></label>
        </div>
        <div className="form-grid two">
          <label><span>Descrição</span><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Descrição opcional" /></label>
          <label><span>Observações</span><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Informações internas" /></label>
        </div>

        <div className="section-heading compact"><div><span className="eyebrow">Variações</span><h3>Cores e estoque inicial</h3></div><button type="button" className="secondary-button" onClick={() => setForm({ ...form, variants: [...form.variants, emptyVariant()] })}><Plus size={17} /> Adicionar cor</button></div>
        <div className="variant-editor-list">
          {form.variants.map((variant, index) => (
            <div className="variant-editor" key={index}>
              <div className="variant-number">{index + 1}</div>
              <label><span>Cor *</span><input value={variant.colorName} onChange={(e) => updateVariant(index, { colorName: e.target.value })} required placeholder="Preto" /></label>
              <label className="color-field"><span>Identificação</span><input type="color" value={variant.colorCode} onChange={(e) => updateVariant(index, { colorCode: e.target.value })} /></label>
              <label><span>Galpão</span><input type="number" min="0" value={variant.galpao} onChange={(e) => updateVariant(index, { galpao: Number(e.target.value) })} /></label>
              <label><span>Estoque mínimo</span><input type="number" min="0" value={variant.minimumStock} onChange={(e) => updateVariant(index, { minimumStock: Number(e.target.value) })} /></label>
              <label className="switch-label"><span>Malharia</span><input type="checkbox" checked={variant.malhariaEnabled} onChange={(e) => updateVariant(index, { malhariaEnabled: e.target.checked })} /><i /></label>
              {variant.malhariaEnabled && <label><span>Qtd. malharia</span><input type="number" min="0" value={variant.malharia} onChange={(e) => updateVariant(index, { malharia: Number(e.target.value) })} /></label>}
              {form.variants.length > 1 && <button type="button" className="icon-button danger" onClick={() => setForm({ ...form, variants: form.variants.filter((_, itemIndex) => itemIndex !== index) })} aria-label="Remover cor"><Trash2 size={18} /></button>}
            </div>
          ))}
        </div>
        <div className="modal-actions"><button type="button" className="ghost-button" onClick={onClose}>Cancelar</button><button className="primary-button" disabled={saving}>{saving ? "Salvando…" : "Cadastrar produto"}</button></div>
      </form>
    </Modal>
  );
}
